import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Pencil, Plus, RefreshCw, Search, UsersRound, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { useAuth } from "@/services/supabase/AuthProvider";
import type { FarmerGroupReference, FarmerGroupCreateInput } from "@/services/contracts";
import type { GeographicUnit } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type Draft = {
  groupCode: string;
  groupName: string;
  chairpersonName: string;
  contactPhone: string;
  registrationDate: string;
  districtId: string;
  countyId: string;
  subcountyId: string;
  parishId: string;
  localityId: string;
};

const emptyDraft = (): Draft => ({
  groupCode: "",
  groupName: "",
  chairpersonName: "",
  contactPhone: "",
  registrationDate: "",
  districtId: "",
  countyId: "",
  subcountyId: "",
  parishId: "",
  localityId: "",
});

export default function FarmerGroupsPage() {
  const auth = useAuth();
  const isSuperAdmin = auth?.aczonContext?.role_code?.toUpperCase() === "SUPER_ADMIN";
  const [groups, setGroups] = useState<FarmerGroupReference[]>([]);
  const [districts, setDistricts] = useState<GeographicUnit[]>([]);
  const [counties, setCounties] = useState<GeographicUnit[]>([]);
  const [subcounties, setSubcounties] = useState<GeographicUnit[]>([]);
  const [parishes, setParishes] = useState<GeographicUnit[]>([]);
  const [localities, setLocalities] = useState<GeographicUnit[]>([]);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadGroups = async () => {
    setLoading(true);
    try { setGroups(await services.farmers.listGroups(true)); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Farmer groups could not be loaded"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadGroups();
    services.geography.listUnits(undefined, "DISTRICT").then(setDistricts).catch(() => setDistricts([]));
  }, []);

  useEffect(() => {
    if (!draft.districtId) { setCounties([]); return; }
    services.geography.listUnits(draft.districtId, "COUNTY").then(setCounties).catch(() => setCounties([]));
  }, [draft.districtId]);

  useEffect(() => {
    if (!draft.countyId) { setSubcounties([]); return; }
    services.geography.listUnits(draft.countyId).then(setSubcounties).catch(() => setSubcounties([]));
  }, [draft.countyId]);

  useEffect(() => {
    if (!draft.subcountyId) { setParishes([]); return; }
    services.geography.listUnits(draft.subcountyId).then(setParishes).catch(() => setParishes([]));
  }, [draft.subcountyId]);

  useEffect(() => {
    if (!draft.parishId) { setLocalities([]); return; }
    services.geography.listUnits(draft.parishId).then((rows) => setLocalities(rows.filter((row) => row.unitType === "VILLAGE" || row.unitType === "CELL"))).catch(() => setLocalities([]));
  }, [draft.parishId]);

  const chosenGeography = draft.localityId || draft.parishId || draft.subcountyId || draft.countyId || draft.districtId;
  const editingGroup = editingGroupId ? groups.find((group) => group.id === editingGroupId) : undefined;
  const effectiveGeography = chosenGeography || editingGroup?.geographicUnitId || "";

  const beginEdit = (group: FarmerGroupReference) => {
    setEditingGroupId(group.id);
    setDraft({
      ...emptyDraft(),
      groupCode: group.groupCode,
      groupName: group.groupName,
      chairpersonName: group.chairpersonName || "",
      contactPhone: group.contactPhone || "",
      registrationDate: group.registrationDate || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingGroupId(null);
    setDraft(emptyDraft());
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isSuperAdmin) return;
    if (!draft.groupCode.trim() || !draft.groupName.trim() || !effectiveGeography) {
      toast.error("Group code, group name and geographic scope are required.");
      return;
    }
    setSaving(true);
    try {
      const input: FarmerGroupCreateInput = {
        groupCode: draft.groupCode.trim(),
        groupName: draft.groupName.trim(),
        geographicUnitId: effectiveGeography,
        chairpersonName: draft.chairpersonName.trim() || undefined,
        contactPhone: draft.contactPhone.trim() || undefined,
        registrationDate: draft.registrationDate || undefined,
      };
      if (editingGroupId) {
        await services.farmers.updateGroup(editingGroupId, {
          groupName: input.groupName,
          geographicUnitId: input.geographicUnitId,
          chairpersonName: input.chairpersonName,
          contactPhone: input.contactPhone,
          registrationDate: input.registrationDate,
        });
        toast.success("Farmer group updated");
      } else {
        await services.farmers.createGroup(input);
        toast.success("Farmer group created");
      }
      setEditingGroupId(null);
      setDraft(emptyDraft());
      await loadGroups();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Farmer group could not be saved");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (group: FarmerGroupReference) => {
    if (!isSuperAdmin) return;
    try {
      await services.farmers.updateGroup(group.id, { status: group.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      toast.success(group.status === "ACTIVE" ? "Farmer group deactivated" : "Farmer group activated");
      await loadGroups();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Farmer group could not be updated");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) => `${group.groupName} ${group.groupCode} ${group.chairpersonName || ""} ${group.status}`.toLowerCase().includes(q));
  }, [groups, search]);

  return <div className="space-y-6" data-testid="farmer-groups-page">
    <PageHeader
      eyebrow="Farmer Registry reference data"
      title="Farmer Groups"
      description="Create and maintain the groups that appear in Farmer Registration. No Supabase or GitHub editing is required."
      action={<Button variant="outline" asChild><Link to="/farmers"><ArrowLeft className="mr-2 size-4" />Farmer Registry</Link></Button>}
    />

    {!isSuperAdmin && <div className="rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm">You can view farmer groups, but only SUPER_ADMIN can create, activate or deactivate them.</div>}

    {isSuperAdmin && <Card>
      <CardHeader><CardTitle className="flex items-center gap-2">{editingGroupId ? <Pencil className="size-4" /> : <Plus className="size-4" />}{editingGroupId ? "Edit farmer group" : "Create farmer group"}</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-2"><Label>Group code</Label><Input value={draft.groupCode} onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, groupCode: e.target.value })} placeholder="e.g. SER-KATETA-01" readOnly={!!editingGroupId} /><p className="text-xs text-muted-foreground">{editingGroupId ? "The group code is a stable identifier and is not changed during editing." : "Use a short, recognizable code."}</p></div>
          <div className="space-y-2 xl:col-span-2"><Label>Group name</Label><Input value={draft.groupName} onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, groupName: e.target.value })} placeholder="e.g. Kateta Parish Coffee Farmers Group" /></div>
          <div className="space-y-2"><Label>District{editingGroupId ? " (change scope)" : ""}</Label><Select value={draft.districtId} onValueChange={(value: string) => setDraft({ ...draft, districtId: value, countyId: "", subcountyId: "", parishId: "", localityId: "" })}><SelectTrigger><SelectValue placeholder={editingGroupId ? "Keep current scope or select new district" : "Select district"} /></SelectTrigger><SelectContent>{districts.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent></Select>{editingGroupId && !chosenGeography && <p className="text-xs text-muted-foreground">Current geographic scope will be retained unless you choose a new hierarchy here.</p>}</div>
          <div className="space-y-2"><Label>County (optional)</Label><Select value={draft.countyId} onValueChange={(value: string) => setDraft({ ...draft, countyId: value, subcountyId: "", parishId: "", localityId: "" })} disabled={!draft.districtId}><SelectTrigger><SelectValue placeholder="Use district scope or select county" /></SelectTrigger><SelectContent>{counties.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>Subcounty / urban unit (optional)</Label><Select value={draft.subcountyId} onValueChange={(value: string) => setDraft({ ...draft, subcountyId: value, parishId: "", localityId: "" })} disabled={!draft.countyId}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>{subcounties.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>Parish / ward (optional)</Label><Select value={draft.parishId} onValueChange={(value: string) => setDraft({ ...draft, parishId: value, localityId: "" })} disabled={!draft.subcountyId}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>{parishes.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>Village / cell (optional)</Label><Select value={draft.localityId} onValueChange={(value: string) => setDraft({ ...draft, localityId: value })} disabled={!draft.parishId}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>{localities.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-2"><Label>Registration date</Label><Input type="date" value={draft.registrationDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, registrationDate: e.target.value })} /></div>
          <div className="space-y-2"><Label>Chairperson</Label><Input value={draft.chairpersonName} onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, chairpersonName: e.target.value })} placeholder="Optional" /></div>
          <div className="space-y-2"><Label>Group contact</Label><Input value={draft.contactPhone} onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft({ ...draft, contactPhone: e.target.value })} placeholder="Optional" /></div>
          <div className="flex items-end gap-2 xl:col-span-3"><Button type="submit" disabled={saving}>{saving ? "Saving..." : editingGroupId ? "Save group changes" : "Create group"}</Button>{editingGroupId && <Button type="button" variant="outline" onClick={cancelEdit}><X className="mr-2 size-4" />Cancel edit</Button>}</div>
        </form>
      </CardContent>
    </Card>}

    <Card>
      <CardContent className="p-0">
        <div className="flex items-center gap-3 border-b p-4">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder="Search group name, code or chairperson" /></div>
          <Button variant="outline" size="icon" onClick={loadGroups} aria-label="Refresh groups"><RefreshCw className="size-4" /></Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Group</TableHead><TableHead>Code</TableHead><TableHead>Chairperson</TableHead><TableHead>Registration</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((group) => <TableRow key={group.id}>
                <TableCell className="font-medium"><div className="flex items-center gap-2"><UsersRound className="size-4 text-primary" />{group.groupName}</div></TableCell>
                <TableCell className="font-mono text-xs">{group.groupCode}</TableCell>
                <TableCell>{group.chairpersonName || "Not recorded"}</TableCell>
                <TableCell>{group.registrationDate || "Not recorded"}</TableCell>
                <TableCell><Badge variant="outline">{group.status}</Badge></TableCell>
                <TableCell className="text-right">{isSuperAdmin && <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => beginEdit(group)}><Pencil className="mr-1 size-3" />Edit</Button><Button size="sm" variant="outline" onClick={() => toggleStatus(group)}>{group.status === "ACTIVE" ? "Deactivate" : "Activate"}</Button></div>}</TableCell>
              </TableRow>)}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No farmer groups found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  </div>;
}
